/**
 * Campaigns page. Bundled via import from app.js (JSX).
 * CampaignsView props: api (API.json), toast, user, timezone, onOpenLead(leadId).
 * AddToCampaignModal props: api, toast, leadIds, onClose, onDone, and optional timezone.
 */
const { useState, useEffect, useRef, useId } = React;

const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DEFAULT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const PAGE_SIZE = 25;
const LOCAL_TIME_HELP = "Calls are placed in each lead's local time zone; we never call before 8 AM or after 9 PM their time.";
const STEPS = ['Name', 'Who to call', 'When to call', 'Review'];
const STATUS_OPTIONS = [
  ['new', 'New'],
  ['interested', 'Interested'],
  ['not_interested', 'Not interested']
];
const INTEREST_OPTIONS = [
  ['hot', 'Hot'],
  ['warm', 'Warm'],
  ['cold', 'Cold']
];
const LEAD_TABS = [
  ['', 'All'],
  ['pending', 'Pending'],
  ['calling', 'Calling'],
  ['completed', 'Completed'],
  ['no_answer', 'No answer'],
  ['failed', 'Failed'],
  ['skipped', 'Skipped']
];
const STATUS_LABEL = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
  canceled: 'Canceled',
  new: 'New',
  interested: 'Interested',
  not_interested: 'Not interested',
  dnc: 'Do not call',
  pending: 'Pending',
  calling: 'Calling',
  no_answer: 'No answer',
  failed: 'Failed',
  skipped: 'Skipped',
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold'
};
const ACTIONS = {
  draft: ['start', 'edit', 'delete'],
  scheduled: ['start', 'edit', 'stop'],
  running: ['pause', 'stop'],
  paused: ['resume', 'edit', 'stop'],
  completed: ['delete'],
  canceled: ['delete']
};
const OPEN_STATUSES = { draft: 1, scheduled: 1, running: 1, paused: 1 };

function cx() {
  return Array.prototype.filter.call(arguments, Boolean).join(' ');
}

function errorMessage(err, fallback) {
  const data = err && err.data;
  const raw = (data && (data.message || data.detail)) || (err && err.message) || '';
  const text = String(raw || '').trim();
  if (!text || text === 'Request failed') return fallback;
  if (/^[a-z0-9_]+$/.test(text)) {
    const words = text.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1) + '.';
  }
  return text;
}

function notify(toast, message, type) {
  if (typeof toast === 'function') toast(message, type || 'info');
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function people(count) {
  const n = num(count);
  return n === 1 ? '1 person' : n + ' people';
}

function emptyStats() {
  return { total: 0, dialed: 0, connected: 0, interested: 0, remaining: 0, minutes: 0, conversion_pct: 0 };
}

function statsOf(campaign, extra) {
  return Object.assign(emptyStats(), (campaign && campaign.stats) || {}, extra || {});
}

function labelOf(value) {
  const key = String(value || '').toLowerCase();
  if (!key) return '—';
  if (STATUS_LABEL[key]) return STATUS_LABEL[key];
  if (key === 'hot') return 'Hot';
  if (key === 'warm') return 'Warm';
  if (key === 'cold') return 'Cold';
  const pretty = key.replace(/_/g, ' ');
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
}

function formatPhone(value) {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value || '');
  return match ? '(' + match[1] + ') ' + match[2] + '-' + match[3] : (value || '');
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 10) / 10;
  if (Math.abs(rounded - Math.round(rounded)) < 0.001) return String(Math.round(rounded));
  return rounded.toFixed(1);
}

function formatPct(value) {
  return formatCount(value) + '%';
}

function canMutate(user) {
  return !user || user.role !== 'viewer';
}

function needsTenant(user) {
  if (!user) return false;
  const role = user.role;
  if (role !== 'super_admin' && role !== 'superadmin') return false;
  if (user.is_impersonation || user.impersonating) return false;
  const tenantId = user.tenant_id != null ? user.tenant_id : user.tenantId;
  return tenantId == null || tenantId === '';
}

function isAssignee(user) {
  if (!user || user.is_active === false || user.is_active === 0) return false;
  const role = String(user.role || '');
  if (!role) return true;
  return role === 'agent' || role === 'tenant_admin' || role === 'supervisor' || role === 'admin';
}

function listNameOf(item) {
  return String((item && (item.name || item.list_name)) || '');
}

function listCount(item) {
  if (!item) return null;
  if (item.leads != null) return item.leads;
  if (item.count != null) return item.count;
  return null;
}

function uniqueIds(ids) {
  const seen = new Set();
  const out = [];
  (ids || []).forEach((id) => {
    if (id == null || id === '') return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(id);
  });
  return out;
}

function sameId(a, b) {
  return String(a) === String(b);
}

function emptyFilters() {
  return {
    status: [],
    interest: [],
    assigned_user_id: '',
    source: '',
    import_batch: '',
    never_called: false,
    not_called_since_days: '',
    created_from: '',
    created_to: '',
    search: ''
  };
}

function emptyAudience(leadIds) {
  const ids = uniqueIds(leadIds);
  return { type: ids.length ? 'manual' : 'filter', list_name: '', filters: emptyFilters(), lead_ids: ids };
}

function defaultSchedule() {
  return {
    startMode: 'now',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    callDays: DEFAULT_DAYS.slice(),
    windowStart: '09:00',
    windowEnd: '20:00',
    maxConcurrency: 3,
    maxAttempts: 3,
    retryDelayMinutes: 60
  };
}

function toggleIn(list, value) {
  return list.indexOf(value) >= 0 ? list.filter((item) => item !== value) : list.concat(value);
}

function safeZone(timeZone) {
  const zone = timeZone || 'America/Los_Angeles';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(new Date());
    return zone;
  } catch (err) {
    return 'America/Los_Angeles';
  }
}

function partsMap(instant, timeZone) {
  const map = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(instant).forEach((part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
  });
  return map;
}

function rollHour(map) {
  let year = Number(map.year);
  let month = Number(map.month);
  let day = Number(map.day);
  let hour = Number(map.hour);
  if (hour === 24) {
    hour = 0;
    const rolled = new Date(Date.UTC(year, month - 1, day + 1));
    year = rolled.getUTCFullYear();
    month = rolled.getUTCMonth() + 1;
    day = rolled.getUTCDate();
  }
  return { year: year, month: month, day: day, hour: hour, minute: Number(map.minute), second: Number(map.second || 0) };
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function wallParts(instant, timeZone) {
  const rolled = rollHour(partsMap(instant, timeZone));
  return { ymd: rolled.year + '-' + pad(rolled.month) + '-' + pad(rolled.day), hm: pad(rolled.hour) + ':' + pad(rolled.minute) };
}

function tzOffsetMs(instant, timeZone) {
  const rolled = rollHour(partsMap(instant, timeZone));
  return Date.UTC(rolled.year, rolled.month - 1, rolled.day, rolled.hour, rolled.minute, rolled.second) - instant.getTime();
}

/**
 * Wall-clock date + time in an IANA zone → UTC.
 * Iterates the zone offset so daylight-saving is honored.
 * A spring-forward gap returns ok:false. A fall-back overlap picks the earlier instant.
 */
function resolveZonedTime(ymd, hm, timeZone) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  const clock = /^(\d{2}):(\d{2})$/.exec(String(hm || '').slice(0, 5));
  if (!match || !clock) return { date: null, ok: false, ambiguous: false };
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  const hh = Number(clock[1]);
  const mm = Number(clock[2]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || hh > 23 || mm > 59) return { date: null, ok: false, ambiguous: false };
  const zone = safeZone(timeZone);
  const desired = Date.UTC(y, mo - 1, d, hh, mm, 0, 0);
  let utc = desired;
  for (let i = 0; i < 4; i++) {
    const next = desired - tzOffsetMs(new Date(utc), zone);
    if (next === utc) break;
    utc = next;
  }
  let date = new Date(utc);
  const wantYmd = y + '-' + pad(mo) + '-' + pad(d);
  const wantHm = pad(hh) + ':' + pad(mm);
  const back = wallParts(date, zone);
  if (back.ymd !== wantYmd || back.hm !== wantHm) return { date: null, ok: false, ambiguous: false };
  let ambiguous = false;
  [-120, -60, 60, 120].forEach((shift) => {
    const other = new Date(date.getTime() + shift * 60000);
    const wall = wallParts(other, zone);
    if (wall.ymd === wantYmd && wall.hm === wantHm && other.getTime() !== date.getTime()) {
      ambiguous = true;
      if (other.getTime() < date.getTime()) date = other;
    }
  });
  return { date: date, ok: true, ambiguous: ambiguous };
}

function formatWhen(iso, timeZone) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: safeZone(timeZone),
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    }).format(date);
  } catch (err) {
    return '';
  }
}

function showWhen(iso, timeZone) {
  return formatWhen(iso, timeZone) || '—';
}

function zoneAbbrev(timeZone, date) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: safeZone(timeZone),
      hour: 'numeric',
      timeZoneName: 'short'
    }).formatToParts(date || new Date());
    const hit = parts.filter((part) => part.type === 'timeZoneName')[0];
    return hit ? hit.value : '';
  } catch (err) {
    return '';
  }
}

function abbrevForDate(timeZone, ymd) {
  if (ymd) {
    const noon = resolveZonedTime(ymd, '12:00', timeZone);
    if (noon.ok) return zoneAbbrev(timeZone, noon.date);
  }
  return zoneAbbrev(timeZone, new Date());
}

function hmToMinutes(hm) {
  const match = /^(\d{2}):(\d{2})$/.exec(String(hm || '').slice(0, 5));
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

function normalizeHm(value, fallback) {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value || '').trim());
  if (!match) return fallback;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return fallback;
  return pad(h) + ':' + pad(m);
}

function formatHmLabel(hm) {
  const mins = hmToMinutes(normalizeHm(hm, ''));
  if (mins == null) return hm || '';
  const date = new Date(Date.UTC(2020, 0, 1, Math.floor(mins / 60), mins % 60));
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(date);
}

function parseDays(value) {
  const parts = Array.isArray(value) ? value : String(value || '').split(/[^A-Za-z]+/);
  return DAY_ORDER.filter((day) => parts.some((part) => String(part).slice(0, 3).toLowerCase() === day.toLowerCase()));
}

function describeDays(days) {
  const set = {};
  (days || []).forEach((day) => { set[day] = 1; });
  const weekdays = DEFAULT_DAYS.every((day) => set[day]) && days.length === 5;
  if (weekdays) return 'Weekdays';
  if (days.length === 7) return 'Every day';
  if (!days.length) return 'No days selected';
  return days.join(', ');
}

function wholeInRange(value, min, max) {
  if (value === '' || value == null) return false;
  const n = Number(value);
  return Number.isInteger(n) && n >= min && n <= max;
}

function filterProblems(filters) {
  if (!filters) return '';
  if (filters.not_called_since_days !== '' && filters.not_called_since_days != null && !filters.never_called) {
    const n = Number(filters.not_called_since_days);
    if (!Number.isInteger(n) || n < 1 || n > 3650) return 'Enter days since the last call as a whole number, or leave it blank.';
  }
  if (filters.created_from && filters.created_to && filters.created_from > filters.created_to) {
    return 'The “added from” date has to be on or before the “added to” date.';
  }
  return '';
}

function filtersPayload(filters) {
  const out = {};
  if (!filters) return out;
  if (filters.status && filters.status.length) out.status = filters.status.slice();
  if (filters.interest && filters.interest.length) out.interest = filters.interest.slice();
  if (filters.assigned_user_id) {
    const raw = String(filters.assigned_user_id).trim();
    const n = Number(raw);
    out.assigned_user_id = Number.isFinite(n) && String(n) === raw ? n : raw;
  }
  if (filters.source && String(filters.source).trim()) out.source = String(filters.source).trim();
  if (filters.import_batch) out.import_batch = filters.import_batch;
  if (filters.never_called) out.never_called = true;
  else if (wholeInRange(filters.not_called_since_days, 1, 3650)) out.not_called_since_days = Number(filters.not_called_since_days);
  if (filters.created_from) out.created_from = filters.created_from;
  if (filters.created_to) out.created_to = filters.created_to;
  if (filters.search && String(filters.search).trim()) out.search = String(filters.search).trim();
  return out;
}

function previewBody(audience) {
  if (!audience) return null;
  if (audience.type === 'list') {
    if (!audience.list_name) return null;
    return { audience_type: 'list', list_name: audience.list_name };
  }
  if (audience.type === 'manual') {
    const leadIds = uniqueIds(audience.lead_ids);
    if (!leadIds.length) return null;
    return { audience_type: 'manual', lead_ids: leadIds };
  }
  if (filterProblems(audience.filters)) return null;
  const filters = filtersPayload(audience.filters);
  if (!Object.keys(filters).length) return null;
  return { audience_type: 'filter', filters: filters };
}

function previewKey(audience) {
  const body = previewBody(audience);
  return body ? JSON.stringify(body) : '';
}

function applyFilters(filters, src) {
  if (!src || typeof src !== 'object' || Array.isArray(src)) return;
  if (Array.isArray(src.status)) filters.status = src.status.map(String);
  else if (src.status && src.status !== 'all') filters.status = [String(src.status)];
  if (Array.isArray(src.interest)) filters.interest = src.interest.map((item) => String(item).toLowerCase());
  if (src.assigned_user_id != null && src.assigned_user_id !== '') filters.assigned_user_id = String(src.assigned_user_id);
  if (src.source) filters.source = String(src.source);
  if (src.import_batch) filters.import_batch = String(src.import_batch);
  if (src.never_called) filters.never_called = true;
  if (src.not_called_since_days != null && src.not_called_since_days !== '') filters.not_called_since_days = String(src.not_called_since_days);
  if (src.created_from) filters.created_from = String(src.created_from).slice(0, 10);
  if (src.created_to) filters.created_to = String(src.created_to).slice(0, 10);
  if (src.search) filters.search = String(src.search);
}

function audienceFromCampaign(campaign) {
  let raw = campaign && campaign.audience_json;
  if (typeof raw === 'string' && raw.trim()) {
    try { raw = JSON.parse(raw); } catch (err) { raw = null; }
  }
  const type = (campaign && campaign.audience_type) || 'filter';
  const filters = emptyFilters();
  let leadIds = [];
  let listName = (campaign && campaign.list_name) || '';
  if (Array.isArray(raw)) leadIds = raw.slice();
  else if (raw && typeof raw === 'object') {
    if (type === 'filter') applyFilters(filters, raw.filters && typeof raw.filters === 'object' ? raw.filters : raw);
    if (Array.isArray(raw.lead_ids)) leadIds = raw.lead_ids.slice();
    if (raw.list_name) listName = raw.list_name;
  }
  return { type: type === 'list' || type === 'manual' || type === 'filter' ? type : 'filter', list_name: listName, filters: filters, lead_ids: uniqueIds(leadIds) };
}

function scheduleFromCampaign(campaign, timeZone) {
  const zone = safeZone(timeZone);
  const start = campaign && campaign.start_at ? wallParts(new Date(campaign.start_at), zone) : null;
  const end = campaign && campaign.end_at ? wallParts(new Date(campaign.end_at), zone) : null;
  const parsed = parseDays(campaign && campaign.call_days);
  const hasDays = campaign && campaign.call_days != null && campaign.call_days !== '';
  const status = campaign && campaign.status;
  return {
    startMode: status === 'paused' || status === 'running' ? 'later' : (campaign && campaign.start_at ? 'later' : 'now'),
    startDate: start ? start.ymd : '',
    startTime: start ? start.hm : '',
    endDate: end ? end.ymd : '',
    endTime: end ? end.hm : '',
    callDays: hasDays ? parsed : DEFAULT_DAYS.slice(),
    windowStart: normalizeHm(campaign && campaign.call_window_start, '09:00'),
    windowEnd: normalizeHm(campaign && campaign.call_window_end, '20:00'),
    maxConcurrency: campaign && campaign.max_concurrency != null ? Number(campaign.max_concurrency) : 3,
    maxAttempts: campaign && campaign.max_attempts != null ? Number(campaign.max_attempts) : 3,
    retryDelayMinutes: campaign && campaign.retry_delay_minutes != null ? Number(campaign.retry_delay_minutes) : 60
  };
}

function hasClock(value) {
  return !!normalizeHm(value, '');
}

function defaultWindowStart(schedule) {
  return normalizeHm(schedule && schedule.windowStart, '09:00');
}

function defaultWindowEnd(schedule) {
  return normalizeHm(schedule && schedule.windowEnd, '') || '23:59';
}

function todayInZone(timeZone) {
  return wallParts(new Date(), safeZone(timeZone)).ymd;
}

function defaultStopDate(schedule, timeZone) {
  if (schedule && schedule.startMode === 'later' && /^\d{4}-\d{2}-\d{2}$/.test(schedule.startDate || '')) {
    return schedule.startDate;
  }
  return todayInZone(timeZone);
}

/** Fill a missing clock when its date is set, and a missing stop date when a stop time is set. */
function completeSchedule(schedule, timeZone) {
  const next = Object.assign({}, schedule || {});
  if (next.startDate && !hasClock(next.startTime)) next.startTime = defaultWindowStart(next);
  if (!next.endDate && hasClock(next.endTime)) next.endDate = defaultStopDate(next, timeZone);
  if (next.endDate && !hasClock(next.endTime)) next.endTime = defaultWindowEnd(next);
  return next;
}

function clockHint(which, hm) {
  const label = formatHmLabel(hm);
  if (!label) return '';
  if (which === 'start') return 'Calls start at ' + label + ' on this day. Change it if you like.';
  return 'Calls stop at ' + label + ' on this day. Change it if you like.';
}

function scheduleIssue(schedule, timeZone, allowStart) {
  const filled = completeSchedule(schedule, timeZone);
  if (allowStart && filled.startMode === 'later') {
    if (!filled.startDate) return { field: 'startDate', message: 'Pick a start date, or choose Start now.' };
    const start = resolveZonedTime(filled.startDate, filled.startTime, timeZone);
    if (!start.ok) return { field: 'startTime', message: 'That start time does not exist because the clocks change. Pick another start time.' };
    if (start.date.getTime() < Date.now() - 60000) return { field: 'startTime', message: 'That start time is already past. Pick a later start time, or choose Start now.' };
  }
  if (filled.endDate || filled.endTime) {
    if (!filled.endDate) return { field: 'endDate', message: 'Pick a stop date, or clear the stop time.' };
    if (!filled.endTime) return { field: 'endTime', message: 'Pick a stop time, or clear the stop date.' };
    const end = resolveZonedTime(filled.endDate, filled.endTime, timeZone);
    if (!end.ok) return { field: 'endTime', message: 'That stop time does not exist because the clocks change. Pick another stop time.' };
    let startMs = Date.now();
    if (allowStart && filled.startMode === 'later') {
      const start = resolveZonedTime(filled.startDate, filled.startTime, timeZone);
      if (start.ok) startMs = start.date.getTime();
    }
    if (end.date.getTime() <= startMs) return { field: 'endTime', message: 'The stop time has to be after the start.' };
  }
  if (!filled.callDays || !filled.callDays.length) return { field: 'callDays', message: 'Pick at least one calling day.' };
  const startMin = hmToMinutes(filled.windowStart);
  const endMin = hmToMinutes(filled.windowEnd);
  if (startMin == null) return { field: 'windowStart', message: 'Enter when the calling window starts.' };
  if (endMin == null) return { field: 'windowEnd', message: 'Enter when the calling window ends.' };
  if (endMin <= startMin) return { field: 'windowEnd', message: 'The calling window has to end later than it starts.' };
  if (!wholeInRange(filled.maxConcurrency, 1, 10)) return { field: 'maxConcurrency', message: 'Choose 1 to 10 calls at the same time.' };
  if (!wholeInRange(filled.maxAttempts, 1, 10)) return { field: 'maxAttempts', message: 'Choose 1 to 10 tries per person.' };
  if (Number(filled.maxAttempts) > 1 && !wholeInRange(filled.retryDelayMinutes, 1, 1440)) {
    return { field: 'retryDelayMinutes', message: 'Enter 1 to 1440 minutes between tries.' };
  }
  return { field: '', message: '' };
}

function scheduleProblem(schedule, timeZone, allowStart) {
  return scheduleIssue(schedule, timeZone, allowStart).message;
}

function ambiguousNote(schedule, timeZone, allowStart) {
  const filled = completeSchedule(schedule, timeZone);
  if (!allowStart || filled.startMode !== 'later' || !filled.startDate || !filled.startTime) return '';
  const start = resolveZonedTime(filled.startDate, filled.startTime, timeZone);
  if (!start.ok || !start.ambiguous) return '';
  return 'Clocks change that day, so this time happens twice. We will use ' + formatWhen(start.date.toISOString(), timeZone) + '.';
}

function joinAnd(bits) {
  if (bits.length === 1) return bits[0];
  if (bits.length === 2) return bits[0] + ' and ' + bits[1];
  return bits.slice(0, -1).join(', ') + ', and ' + bits[bits.length - 1];
}

function previewCopy(preview) {
  if (!preview) return '';
  const eligible = num(preview.eligible);
  const excluded = preview.excluded || {};
  const bits = [];
  function add(count, one, many) {
    const n = num(count);
    if (n === 1) bits.push(one);
    else if (n > 1) bits.push(many.replace('{n}', String(n)));
  }
  add(excluded.dnc, '1 is on the do-not-call list', '{n} are on the do-not-call list');
  add(excluded.no_phone, '1 has no phone number', '{n} have no phone number');
  add(excluded.deleted, '1 was removed from your leads', '{n} were removed from your leads');
  add(excluded.already_in_active_campaign, '1 is already in a campaign that is still going', '{n} are already in a campaign that is still going');
  const head = eligible === 0 ? 'No one can be called.' : (eligible === 1 ? '1 person can be called.' : eligible + ' people can be called.');
  if (!bits.length) return head;
  return head + ' Leaving out ' + joinAnd(bits) + '.';
}

function progressPct(stats) {
  const total = num(stats.total);
  const dialed = num(stats.dialed);
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((dialed / total) * 100)));
}

function progressLabel(stats) {
  const total = num(stats.total);
  if (!total) return 'No leads yet';
  return num(stats.dialed) + ' of ' + total + ' called';
}

function audienceBlurb(campaign) {
  if (!campaign) return '';
  if (campaign.audience_type === 'list') return campaign.list_name ? 'List: ' + campaign.list_name : 'Imported list';
  if (campaign.audience_type === 'manual') return 'People you picked';
  if (campaign.audience_type === 'filter') return 'People matching a filter';
  return 'Audience not set';
}

function timingBlurb(campaign, timeZone) {
  if (!campaign) return '';
  if (campaign.status === 'running') return campaign.started_at ? 'Started ' + showWhen(campaign.started_at, timeZone) : 'Running now';
  if (campaign.status === 'scheduled') return campaign.start_at ? 'Starts ' + showWhen(campaign.start_at, timeZone) : 'Scheduled';
  if (campaign.status === 'paused') return 'Paused';
  if (campaign.status === 'completed') return campaign.completed_at ? 'Finished ' + showWhen(campaign.completed_at, timeZone) : 'Finished';
  if (campaign.status === 'canceled') return 'Stopped';
  if (campaign.start_at) return 'Starts ' + showWhen(campaign.start_at, timeZone);
  return 'Not scheduled';
}

function actionLabel(action, status) {
  if (action === 'start' && status === 'scheduled') return 'Start now';
  if (action === 'start') return 'Start';
  if (action === 'pause') return 'Pause';
  if (action === 'resume') return 'Resume';
  if (action === 'stop') return 'Stop';
  if (action === 'edit') return 'Edit';
  if (action === 'delete') return 'Delete';
  return action;
}

function actionToast(action) {
  if (action === 'start') return 'Campaign started.';
  if (action === 'pause') return 'Campaign paused.';
  if (action === 'resume') return 'Campaign resumed.';
  if (action === 'stop') return 'Campaign stopped.';
  if (action === 'delete') return 'Campaign deleted.';
  return 'Saved.';
}

function sampleRowId(row) {
  if (!row) return null;
  if (row.id != null) return row.id;
  if (row.lead_id != null) return row.lead_id;
  return null;
}

function unwrapCampaign(data) {
  if (!data) return { campaign: null, stats: emptyStats() };
  if (data.campaign) return { campaign: data.campaign, stats: data.stats || data.campaign.stats || emptyStats() };
  return { campaign: data, stats: data.stats || emptyStats() };
}

function useDialog(onClose) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function focusables() {
      return Array.from(node.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter((el) => {
        return !el.disabled && el.getAttribute('aria-hidden') !== 'true' && el.getClientRects().length > 0;
      });
    }
    const preferred = node.querySelector('[data-autofocus]');
    if (preferred && typeof preferred.focus === 'function') preferred.focus();
    else {
      const first = focusables()[0];
      if (first) first.focus();
    }
    function onKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = previousOverflow;
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, []);
  return ref;
}

function useLookups(api) {
  const [users, setUsers] = useState([]);
  const [usersHidden, setUsersHidden] = useState(false);
  const [lists, setLists] = useState([]);
  const [listsState, setListsState] = useState('loading');
  const [listsError, setListsError] = useState('');
  useEffect(() => {
    let cancelled = false;
    api('GET', '/api/users')
      .then((result) => {
        if (cancelled) return;
        const rows = (result && (result.users || result.items)) || [];
        setUsers(Array.isArray(rows) ? rows.filter(isAssignee) : []);
      })
      .catch(() => { if (!cancelled) setUsersHidden(true); });
    api('GET', '/api/lead-lists')
      .then((result) => {
        if (cancelled) return;
        setLists((result && result.items) || []);
        setListsState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setListsState('error');
        setListsError(errorMessage(err, 'Could not load imported lists.'));
      });
    return () => { cancelled = true; };
  }, []);
  return { users: users, usersHidden: usersHidden, lists: lists, listsState: listsState, listsError: listsError };
}

function Modal({ title, children, footer, onClose, wide }) {
  const titleId = useId();
  const ref = useDialog(onClose);
  return (
    <div className="cmp-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className={cx('cmp-dialog', wide && 'cmp-dialog--wide')} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <div className="cmp-dialog-head">
          <h2 id={titleId} className="cmp-dialog-title">{title}</h2>
          <button type="button" className="cmp-icon" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="cmp-dialog-body">{children}</div>
        {footer ? <div className="cmp-dialog-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

function ConfirmDialog({ title, body, confirmLabel, busy, onConfirm, onClose }) {
  return (
    <Modal
      title={title}
      onClose={busy ? function () {} : onClose}
      footer={(
        <React.Fragment>
          <button type="button" className="cmp-btn" data-autofocus onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="cmp-btn cmp-btn--danger" onClick={onConfirm} disabled={busy}>{busy ? 'Working…' : confirmLabel}</button>
        </React.Fragment>
      )}
    >
      <p className="cmp-confirm-copy">{body}</p>
    </Modal>
  );
}

function StatusPill({ status }) {
  const key = String(status || 'draft').toLowerCase();
  return <span className={cx('cmp-pill', 'cmp-pill--' + key)}>{labelOf(key)}</span>;
}

function ProgressBar({ stats }) {
  const pct = progressPct(stats);
  const label = progressLabel(stats);
  return (
    <div className="cmp-progress">
      <div className="cmp-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
        <span className="cmp-bar-fill" style={{ width: pct + '%' }} />
      </div>
      <div className="cmp-bar-label">{label}</div>
    </div>
  );
}

function CampaignActions({ campaign, user, busy, onAction }) {
  if (!canMutate(user)) return null;
  const actions = ACTIONS[String(campaign.status || '').toLowerCase()] || [];
  if (!actions.length) return null;
  return (
    <div className="cmp-actions">
      {actions.map((action) => {
        const primary = action === 'start' || action === 'pause' || action === 'resume';
        const danger = action === 'stop' || action === 'delete';
        return (
          <button
            key={action}
            type="button"
            className={cx('cmp-btn', primary && 'cmp-btn--primary', danger && 'cmp-btn--danger')}
            disabled={busy}
            onClick={() => onAction(campaign, action)}
          >
            {actionLabel(action, campaign.status)}
          </button>
        );
      })}
    </div>
  );
}

function CheckGroup({ legend, options, selected, onToggle, idPrefix }) {
  return (
    <fieldset className="cmp-field">
      <legend className="cmp-label">{legend}</legend>
      <div className="cmp-checks">
        {options.map(([value, label]) => {
          const id = idPrefix + '-' + value;
          return (
            <label key={value} className="cmp-check" htmlFor={id}>
              <input id={id} type="checkbox" checked={selected.indexOf(value) >= 0} onChange={() => onToggle(value)} />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function AudiencePicker({ api, value, onChange, onPreview, users, usersHidden, lists, listsState, listsError }) {
  const uid = useId();
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [leadsState, setLeadsState] = useState('idle');
  const [leadsError, setLeadsError] = useState('');
  const pageBox = useRef(null);
  const key = previewKey(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    if (!key) {
      onPreviewRef.current({ key: '', state: 'idle', data: null, error: '' });
      return undefined;
    }
    onPreviewRef.current({ key: key, state: 'loading', data: null, error: '' });
    const timer = setTimeout(() => {
      let body = null;
      try { body = JSON.parse(key); } catch (err) { body = null; }
      api('POST', '/api/campaigns/preview', body)
        .then((data) => { if (!cancelled) onPreviewRef.current({ key: key, state: 'ready', data: data, error: '' }); })
        .catch((err) => { if (!cancelled) onPreviewRef.current({ key: key, state: 'error', data: null, error: errorMessage(err, 'Could not check who would be called.') }); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key]);

  useEffect(() => {
    if (value.type !== 'manual') return undefined;
    let cancelled = false;
    setLeadsState('loading');
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(PAGE_SIZE));
    if (debounced) params.set('q', debounced);
    api('GET', '/api/leads?' + params.toString())
      .then((data) => {
        if (cancelled) return;
        setRows((data && data.items) || []);
        setTotal(num(data && data.total));
        setLeadsState('ready');
        setLeadsError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setLeadsState('error');
        setLeadsError(errorMessage(err, 'Could not load leads.'));
      });
    return () => { cancelled = true; };
  }, [value.type, page, debounced]);

  const selected = new Set((value.lead_ids || []).map(String));
  const allOnPage = rows.length > 0 && rows.every((row) => selected.has(String(row.id)));
  const someOnPage = rows.some((row) => selected.has(String(row.id)));
  useEffect(() => {
    if (pageBox.current) pageBox.current.indeterminate = someOnPage && !allOnPage;
  }, [someOnPage, allOnPage, rows]);

  function patch(next) {
    onChange(Object.assign({}, value, next));
  }
  function patchFilters(next) {
    onChange(Object.assign({}, value, { filters: Object.assign({}, value.filters, next) }));
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filters = value.filters || emptyFilters();

  return (
    <div className="cmp-audience-picker">
      <div className="cmp-choices" role="radiogroup" aria-label="Who should we call?">
        {[
          ['filter', 'Pick leads by filter', 'Use the leads you already have. Narrow by status, interest, assignee, and more.'],
          ['list', 'Use an imported list', 'Call everyone from a CSV you already imported.'],
          ['manual', 'Choose leads by hand', 'Search your leads and tick the people to include.']
        ].map(([type, title, copy]) => (
          <label key={type} className={cx('cmp-choice', value.type === type && 'cmp-choice--on')}>
            <input type="radio" name={uid + '-audience'} value={type} checked={value.type === type} onChange={() => patch({ type: type })} />
            <span>
              <span className="cmp-choice-title">{title}</span>
              <span className="cmp-choice-copy">{copy}</span>
            </span>
          </label>
        ))}
      </div>

      {value.type === 'filter' ? (
        <div className="cmp-filter">
          <CheckGroup legend="Status" options={STATUS_OPTIONS} selected={filters.status} idPrefix={uid + '-status'} onToggle={(item) => patchFilters({ status: toggleIn(filters.status, item) })} />
          <CheckGroup legend="Interest" options={INTEREST_OPTIONS} selected={filters.interest} idPrefix={uid + '-interest'} onToggle={(item) => patchFilters({ interest: toggleIn(filters.interest, item) })} />
          <div className="cmp-row">
            {usersHidden ? (
              <p className="cmp-hint">Assignee is hidden because your login cannot view the team list.</p>
            ) : (
              <div className="cmp-field">
                <label className="cmp-label" htmlFor={uid + '-assignee'}>Assignee</label>
                <select id={uid + '-assignee'} className="cmp-select" value={filters.assigned_user_id} onChange={(event) => patchFilters({ assigned_user_id: event.target.value })}>
                  <option value="">Anyone</option>
                  {filters.assigned_user_id && !users.some((user) => sameId(user.id, filters.assigned_user_id)) ? <option value={filters.assigned_user_id}>Current assignee</option> : null}
                  {users.map((user) => <option key={user.id} value={user.id}>{user.name || user.email}</option>)}
                </select>
              </div>
            )}
            <div className="cmp-field">
              <label className="cmp-label" htmlFor={uid + '-source'}>Source</label>
              <input id={uid + '-source'} className="cmp-input" value={filters.source} onChange={(event) => patchFilters({ source: event.target.value })} placeholder="For example: website" />
            </div>
          </div>
          <div className="cmp-field">
            <label className="cmp-label" htmlFor={uid + '-batch'}>Import list</label>
            <select id={uid + '-batch'} className="cmp-select" value={filters.import_batch} onChange={(event) => patchFilters({ import_batch: event.target.value })}>
              <option value="">Any list</option>
              {filters.import_batch && !lists.some((item) => listNameOf(item) === filters.import_batch) ? <option value={filters.import_batch}>{filters.import_batch}</option> : null}
              {lists.map((item, index) => {
                const name = listNameOf(item);
                const count = listCount(item);
                return <option key={name + '-' + index} value={name}>{name}{count != null ? ' (' + count + ')' : ''}</option>;
              })}
            </select>
            {listsState === 'error' ? <p className="cmp-hint">{listsError}</p> : null}
          </div>
          <label className="cmp-check" htmlFor={uid + '-never'}>
            <input id={uid + '-never'} type="checkbox" checked={!!filters.never_called} onChange={(event) => patchFilters({ never_called: event.target.checked })} />
            <span>Never called</span>
          </label>
          <div className="cmp-field">
            <label className="cmp-label" htmlFor={uid + '-since'}>Not called in this many days</label>
            <input id={uid + '-since'} className="cmp-input cmp-input--short" type="number" min="1" max="3650" inputMode="numeric" disabled={!!filters.never_called} value={filters.not_called_since_days} onChange={(event) => patchFilters({ not_called_since_days: event.target.value })} />
          </div>
          <div className="cmp-row">
            <div className="cmp-field">
              <label className="cmp-label" htmlFor={uid + '-from'}>Added from</label>
              <input id={uid + '-from'} className="cmp-input" type="date" value={filters.created_from} onChange={(event) => patchFilters({ created_from: event.target.value })} />
            </div>
            <div className="cmp-field">
              <label className="cmp-label" htmlFor={uid + '-to'}>Added to</label>
              <input id={uid + '-to'} className="cmp-input" type="date" value={filters.created_to} onChange={(event) => patchFilters({ created_to: event.target.value })} />
            </div>
          </div>
          <div className="cmp-field">
            <label className="cmp-label" htmlFor={uid + '-search'}>Search</label>
            <input id={uid + '-search'} className="cmp-input" type="search" value={filters.search} onChange={(event) => patchFilters({ search: event.target.value })} placeholder="Name, company, or phone" />
          </div>
          {filterProblems(filters) ? <p className="cmp-warn" role="status">{filterProblems(filters)}</p> : null}
        </div>
      ) : null}

      {value.type === 'list' ? (
        <div className="cmp-field">
          <label className="cmp-label" htmlFor={uid + '-list'}>Imported list</label>
          {listsState === 'loading' ? <p className="cmp-hint">Loading lists…</p> : null}
          {listsState === 'error' ? <p className="cmp-warn">{listsError}</p> : null}
          {listsState === 'ready' && !lists.length ? <p className="cmp-hint">You don’t have any imported lists yet. Import a CSV from Leads, or pick people another way.</p> : null}
          <select id={uid + '-list'} className="cmp-select" value={value.list_name} onChange={(event) => patch({ list_name: event.target.value })}>
            <option value="">Choose a list</option>
            {value.list_name && !lists.some((item) => listNameOf(item) === value.list_name) ? <option value={value.list_name}>{value.list_name}</option> : null}
            {lists.map((item, index) => {
              const name = listNameOf(item);
              const count = listCount(item);
              return <option key={name + '-' + index} value={name}>{name}{count != null ? ' (' + count + ' leads)' : ''}</option>;
            })}
          </select>
        </div>
      ) : null}

      {value.type === 'manual' ? (
        <div className="cmp-leadpick">
          <div className="cmp-field">
            <label className="cmp-label" htmlFor={uid + '-q'}>Search leads</label>
            <input id={uid + '-q'} className="cmp-input" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, company, or phone" />
          </div>
          <p className="cmp-hint">{value.lead_ids.length === 1 ? '1 person selected.' : value.lead_ids.length + ' people selected.'}</p>
          {value.lead_ids.length ? (
            <button type="button" className="cmp-btn" onClick={() => patch({ lead_ids: [] })}>Clear selection</button>
          ) : null}
          {leadsState === 'error' ? <p className="cmp-warn">{leadsError}</p> : null}
          {leadsState === 'loading' ? <p className="cmp-hint">Loading leads…</p> : null}
          {leadsState === 'ready' && !rows.length ? <p className="cmp-hint">No leads match that search.</p> : null}
          {rows.length ? (
            <div>
              <label className="cmp-check" htmlFor={uid + '-page'}>
                <input id={uid + '-page'} ref={pageBox} type="checkbox" checked={allOnPage} onChange={(event) => {
                  const pageIds = rows.map((row) => row.id);
                  if (event.target.checked) patch({ lead_ids: uniqueIds(value.lead_ids.concat(pageIds)) });
                  else {
                    const drop = new Set(pageIds.map(String));
                    patch({ lead_ids: value.lead_ids.filter((id) => !drop.has(String(id))) });
                  }
                }} />
                <span>Select everyone on this page</span>
              </label>
              <ul className="cmp-people">
                {rows.map((row) => {
                  const id = uid + '-lead-' + String(row.id).replace(/[^A-Za-z0-9_-]/g, '');
                  const on = selected.has(String(row.id));
                  const meta = [row.company, formatPhone(row.phone), labelOf(row.dnc_flag ? 'dnc' : row.status)].filter(Boolean).join(' · ');
                  return (
                    <li key={row.id} className="cmp-person">
                      <input id={id} type="checkbox" checked={on} onChange={() => patch({ lead_ids: on ? value.lead_ids.filter((item) => !sameId(item, row.id)) : uniqueIds(value.lead_ids.concat([row.id])) })} />
                      <label htmlFor={id}>
                        <span className="cmp-person-name">{row.name || 'Unnamed lead'}</span>
                        {meta ? <span className="cmp-person-meta">{meta}</span> : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
          {total > PAGE_SIZE ? (
            <div className="cmp-pager">
              <button type="button" className="cmp-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
              <span>Page {page} of {pageCount}</span>
              <button type="button" className="cmp-btn" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PreviewPanel({ audience, preview }) {
  const key = previewKey(audience);
  let body = null;
  if (!key) {
    const problem = audience && audience.type === 'filter' ? filterProblems(audience.filters) : '';
    body = <p>{problem || 'Choose who to call. We’ll show how many people can actually be dialed.'}</p>;
  } else if (!preview || preview.key !== key || preview.state === 'loading' || preview.state === 'idle') {
    body = <p>Checking who can be called…</p>;
  } else if (preview.state === 'error') {
    body = <p className="cmp-warn">{preview.error}</p>;
  } else {
    const data = preview.data || {};
    const sample = data.sample || [];
    body = (
      <React.Fragment>
        <p className="cmp-preview-count">{previewCopy(data)}</p>
        {sample.length ? (
          <React.Fragment>
            <p className="cmp-hint">A sample of who matches:</p>
            <ul className="cmp-sample">
              {sample.map((row, index) => {
                const id = sampleRowId(row);
                const meta = [row.company, formatPhone(row.phone), labelOf(row.status)].filter(Boolean).join(' · ');
                return (
                  <li key={id != null ? id : index}>
                    <span className="cmp-person-name">{row.name || 'Unnamed lead'}</span>
                    {meta ? <span className="cmp-person-meta">{meta}</span> : null}
                  </li>
                );
              })}
            </ul>
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  }
  return <div className="cmp-preview" aria-live="polite">{body}</div>;
}

function FieldError({ id, message }) {
  if (!message) return null;
  return <p className="cmp-warn" id={id}>{message}</p>;
}

function ClockControl({ id, label, active, value, onChange, hint, error, emptyText }) {
  const hintId = id + '-hint';
  const errorId = id + '-error';
  const described = [];
  if (active && hint) described.push(hintId);
  if (error) described.push(errorId);
  return (
    <div className="cmp-field">
      {active ? <label className="cmp-label" htmlFor={id}>{label}</label> : <span className="cmp-label">{label}</span>}
      {active ? (
        <input
          id={id}
          className="cmp-input"
          type="time"
          step="60"
          value={value}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={described.length ? described.join(' ') : undefined}
          onChange={(event) => onChange(event.target.value.slice(0, 5))}
        />
      ) : <p className="cmp-time-empty">{emptyText}</p>}
      {active && hint ? <p className="cmp-hint" id={hintId}>{hint}</p> : null}
      <FieldError id={errorId} message={error} />
    </div>
  );
}

function ScheduleFields({ schedule, onChange, timeZone, allowStart }) {
  const uid = useId();
  const filled = completeSchedule(schedule, timeZone);
  const issue = scheduleIssue(schedule, timeZone, allowStart);
  function message(field) {
    return issue.field === field ? issue.message : '';
  }
  useEffect(() => {
    if (
      filled.startDate === schedule.startDate &&
      filled.startTime === schedule.startTime &&
      filled.endDate === schedule.endDate &&
      filled.endTime === schedule.endTime
    ) return;
    onChange(filled);
  }, [filled.startDate, filled.startTime, filled.endDate, filled.endTime, schedule, onChange]);
  function patch(next) {
    onChange(completeSchedule(Object.assign({}, schedule, next), timeZone));
  }
  function onStartDate(value) {
    if (!value) {
      onChange(Object.assign({}, schedule, { startDate: '', startTime: '' }));
      return;
    }
    onChange(completeSchedule(Object.assign({}, schedule, { startDate: value }), timeZone));
  }
  function onEndDate(value) {
    if (!value) {
      onChange(Object.assign({}, schedule, { endDate: '', endTime: '' }));
      return;
    }
    onChange(completeSchedule(Object.assign({}, schedule, { endDate: value }), timeZone));
  }
  function onStartTime(value) {
    const startTime = normalizeHm(value, '') || defaultWindowStart(schedule);
    onChange(Object.assign({}, schedule, { startTime: startTime }));
  }
  function onEndTime(value) {
    const endTime = normalizeHm(value, '') || defaultWindowEnd(schedule);
    onChange(completeSchedule(Object.assign({}, schedule, { endTime: endTime }), timeZone));
  }
  const abbrev = abbrevForDate(timeZone, schedule.startMode === 'later' ? schedule.startDate : '');
  const zoneLine = abbrev ? 'Dates and times are in ' + abbrev + ' (' + safeZone(timeZone) + ').' : 'Dates and times use ' + safeZone(timeZone) + '.';
  const note = ambiguousNote(schedule, timeZone, allowStart);
  const stopDateShown = schedule.endDate || (hasClock(schedule.endTime) ? filled.endDate : '');
  const startDateError = message('startDate');
  const endDateError = message('endDate');
  return (
    <div>
      {allowStart ? (
        <fieldset className="cmp-field">
          <legend className="cmp-label">When should it start?</legend>
          <div className="cmp-checks">
            <label className="cmp-check" htmlFor={uid + '-now'}>
              <input id={uid + '-now'} type="radio" name={uid + '-start'} checked={schedule.startMode === 'now'} onChange={() => patch({ startMode: 'now' })} />
              <span>Start now</span>
            </label>
            <label className="cmp-check" htmlFor={uid + '-later'}>
              <input id={uid + '-later'} type="radio" name={uid + '-start'} checked={schedule.startMode === 'later'} onChange={() => patch({ startMode: 'later' })} />
              <span>Start at a set time</span>
            </label>
          </div>
        </fieldset>
      ) : <p className="cmp-hint">This campaign has already started. You can still change the days, hours, and stop time.</p>}
      {allowStart && schedule.startMode === 'later' ? (
        <div className="cmp-row">
          <div className="cmp-field">
            <label className="cmp-label" htmlFor={uid + '-date'}>Start date</label>
            <input id={uid + '-date'} className="cmp-input" type="date" value={schedule.startDate} aria-invalid={startDateError ? 'true' : undefined} aria-describedby={startDateError ? uid + '-date-error' : undefined} onChange={(event) => onStartDate(event.target.value)} />
            <FieldError id={uid + '-date-error'} message={startDateError} />
          </div>
          <ClockControl id={uid + '-time'} label="Start time" active={!!schedule.startDate} value={filled.startTime} onChange={onStartTime} hint={clockHint('start', filled.startTime)} error={message('startTime')} emptyText="No start time" />
        </div>
      ) : null}
      <p className="cmp-hint">{zoneLine}</p>
      {note ? <p className="cmp-warn" role="status">{note}</p> : null}
      <div className="cmp-row">
        <div className="cmp-field">
          <label className="cmp-label" htmlFor={uid + '-end-date'}>Stop date (optional)</label>
          <input id={uid + '-end-date'} className="cmp-input" type="date" value={stopDateShown} aria-invalid={endDateError ? 'true' : undefined} aria-describedby={endDateError ? uid + '-end-date-error' : undefined} onChange={(event) => onEndDate(event.target.value)} />
          <FieldError id={uid + '-end-date-error'} message={endDateError} />
          {(schedule.endDate || schedule.endTime) ? (
            <button type="button" className="cmp-link" aria-label="Clear stop date and time" onClick={() => onChange(Object.assign({}, schedule, { endDate: '', endTime: '' }))}>Clear</button>
          ) : null}
        </div>
        <ClockControl id={uid + '-end-time'} label="Stop time" active={!!stopDateShown} value={filled.endTime} onChange={onEndTime} hint={clockHint('stop', filled.endTime)} error={message('endTime')} emptyText="No stop time" />
      </div>
      <fieldset className="cmp-field">
        <legend className="cmp-label">Calling days</legend>
        <div className="cmp-chips" role="group" aria-label="Calling days">
          {DAY_ORDER.map((day) => {
            const on = schedule.callDays.indexOf(day) >= 0;
            return (
              <button key={day} type="button" className={cx('cmp-chip', on && 'cmp-chip--on')} aria-pressed={on} onClick={() => patch({ callDays: toggleIn(schedule.callDays, day) })}>{day}</button>
            );
          })}
        </div>
        <FieldError id={uid + '-days-error'} message={message('callDays')} />
      </fieldset>
      <div className="cmp-row">
        <div className="cmp-field">
          <label className="cmp-label" htmlFor={uid + '-win-start'}>Calling window starts</label>
          <input id={uid + '-win-start'} className="cmp-input" type="time" step="60" value={schedule.windowStart} aria-invalid={message('windowStart') ? 'true' : undefined} aria-describedby={message('windowStart') ? uid + '-win-start-error' : undefined} onChange={(event) => patch({ windowStart: event.target.value.slice(0, 5) })} />
          <FieldError id={uid + '-win-start-error'} message={message('windowStart')} />
        </div>
        <div className="cmp-field">
          <label className="cmp-label" htmlFor={uid + '-win-end'}>Calling window ends</label>
          <input id={uid + '-win-end'} className="cmp-input" type="time" step="60" value={schedule.windowEnd} aria-invalid={message('windowEnd') ? 'true' : undefined} aria-describedby={message('windowEnd') ? uid + '-win-end-error' : undefined} onChange={(event) => patch({ windowEnd: event.target.value.slice(0, 5) })} />
          <FieldError id={uid + '-win-end-error'} message={message('windowEnd')} />
        </div>
      </div>
      <p className="cmp-hint">{LOCAL_TIME_HELP}</p>
      <div className="cmp-row">
        <div className="cmp-field">
          <label className="cmp-label" htmlFor={uid + '-pace'}>Calls at the same time</label>
          <input id={uid + '-pace'} className="cmp-input cmp-input--short" type="number" min="1" max="10" inputMode="numeric" value={schedule.maxConcurrency} aria-invalid={message('maxConcurrency') ? 'true' : undefined} aria-describedby={message('maxConcurrency') ? uid + '-pace-error' : undefined} onChange={(event) => patch({ maxConcurrency: event.target.value === '' ? '' : Number(event.target.value) })} />
          <p className="cmp-hint">From 1 to 10.</p>
          <FieldError id={uid + '-pace-error'} message={message('maxConcurrency')} />
        </div>
        <div className="cmp-field">
          <label className="cmp-label" htmlFor={uid + '-tries'}>Tries per person</label>
          <input id={uid + '-tries'} className="cmp-input cmp-input--short" type="number" min="1" max="10" inputMode="numeric" value={schedule.maxAttempts} aria-invalid={message('maxAttempts') ? 'true' : undefined} aria-describedby={message('maxAttempts') ? uid + '-tries-error' : undefined} onChange={(event) => patch({ maxAttempts: event.target.value === '' ? '' : Number(event.target.value) })} />
          <FieldError id={uid + '-tries-error'} message={message('maxAttempts')} />
        </div>
      </div>
      <div className="cmp-field">
        <label className="cmp-label" htmlFor={uid + '-gap'}>Minutes between tries</label>
        <input id={uid + '-gap'} className="cmp-input cmp-input--short" type="number" min="1" max="1440" inputMode="numeric" disabled={Number(schedule.maxAttempts) <= 1} value={schedule.retryDelayMinutes} aria-invalid={message('retryDelayMinutes') ? 'true' : undefined} aria-describedby={message('retryDelayMinutes') ? uid + '-gap-error' : undefined} onChange={(event) => patch({ retryDelayMinutes: event.target.value === '' ? '' : Number(event.target.value) })} />
        <FieldError id={uid + '-gap-error'} message={message('retryDelayMinutes')} />
        <p className="cmp-hint">{Number(schedule.maxAttempts) <= 1 ? 'We will only call each person once.' : 'Wait this long before trying the same person again. Up to 24 hours.'}</p>
      </div>
    </div>
  );
}

function filterSummary(filters, users) {
  const items = [];
  if (filters.status.length) items.push('Status: ' + filters.status.map(labelOf).join(', '));
  if (filters.interest.length) items.push('Interest: ' + filters.interest.map((item) => labelOf(item)).join(', '));
  if (filters.assigned_user_id) {
    const user = users.filter((item) => sameId(item.id, filters.assigned_user_id))[0];
    items.push('Assignee: ' + (user ? (user.name || user.email) : 'selected teammate'));
  }
  if (String(filters.source || '').trim()) items.push('Source: ' + String(filters.source).trim());
  if (filters.import_batch) items.push('Import list: ' + filters.import_batch);
  if (filters.never_called) items.push('Never called');
  else if (wholeInRange(filters.not_called_since_days, 1, 3650)) items.push('Not called in ' + Number(filters.not_called_since_days) + ' days');
  if (filters.created_from || filters.created_to) items.push('Added ' + (filters.created_from || 'any time') + ' to ' + (filters.created_to || 'any time'));
  if (String(filters.search || '').trim()) items.push('Search: ' + String(filters.search).trim());
  return items;
}

function ReviewStep({ name, audience, schedule, preview, users, timeZone, allowStart, onJump }) {
  const who = audience.type === 'list'
    ? 'Imported list: ' + (audience.list_name || 'not chosen')
    : audience.type === 'manual'
      ? (audience.lead_ids.length === 1 ? '1 person you picked' : audience.lead_ids.length + ' people you picked')
      : 'People matching a filter';
  const filterLines = audience.type === 'filter' ? filterSummary(audience.filters, users) : [];
  const fresh = preview && preview.key === previewKey(audience) && preview.state === 'ready';
  const filled = completeSchedule(schedule, timeZone);
  let when = 'Starts as soon as you confirm.';
  if (allowStart && filled.startMode === 'later') {
    const start = resolveZonedTime(filled.startDate, filled.startTime, timeZone);
    when = start.ok ? 'Starts ' + formatWhen(start.date.toISOString(), timeZone) : 'Start time needs another look.';
  } else if (!allowStart) when = 'Already started. New hours apply from here on.';
  let stop = 'No stop date.';
  if (filled.endDate && filled.endTime) {
    const end = resolveZonedTime(filled.endDate, filled.endTime, timeZone);
    stop = end.ok ? 'Stops ' + formatWhen(end.date.toISOString(), timeZone) : 'Stop time needs another look.';
  }
  const tries = Number(filled.maxAttempts) <= 1
    ? 'One try per person.'
    : 'Up to ' + filled.maxAttempts + ' tries per person, ' + filled.retryDelayMinutes + ' minutes apart.';
  const days = DAY_ORDER.filter((day) => filled.callDays.indexOf(day) >= 0);
  return (
    <div className="cmp-review">
      <p>Check this, then confirm.</p>
      <div className="cmp-review-block">
        <div className="cmp-review-top"><h3>Name</h3><button type="button" className="cmp-btn" onClick={() => onJump(0)}>Change</button></div>
        <p>{name.trim()}</p>
      </div>
      <div className="cmp-review-block">
        <div className="cmp-review-top"><h3>Who</h3><button type="button" className="cmp-btn" onClick={() => onJump(1)}>Change</button></div>
        <p>{who}</p>
        {filterLines.length ? <ul className="cmp-summary">{filterLines.map((line) => <li key={line}>{line}</li>)}</ul> : null}
        <p>{fresh ? previewCopy(preview.data) : 'Audience check is not ready yet.'}</p>
      </div>
      <div className="cmp-review-block">
        <div className="cmp-review-top"><h3>When</h3><button type="button" className="cmp-btn" onClick={() => onJump(2)}>Change</button></div>
        <p>{when}</p>
        <p>{stop}</p>
        <p>{describeDays(days)}, {formatHmLabel(filled.windowStart)} to {formatHmLabel(filled.windowEnd)}.</p>
        <p>Up to {filled.maxConcurrency} calls at the same time. {tries}</p>
        <p className="cmp-hint">{LOCAL_TIME_HELP}</p>
      </div>
    </div>
  );
}

function buildWriteBody({ name, audience, schedule, timeZone, mode, allowStart }) {
  const filled = completeSchedule(schedule, timeZone);
  const days = DAY_ORDER.filter((day) => filled.callDays.indexOf(day) >= 0);
  const body = {
    name: name.trim(),
    audience_type: audience.type,
    timezone: safeZone(timeZone),
    call_days: days,
    call_window_start: normalizeHm(filled.windowStart, '09:00'),
    call_window_end: normalizeHm(filled.windowEnd, '20:00'),
    max_concurrency: Number(filled.maxConcurrency),
    max_attempts: Number(filled.maxAttempts),
    retry_delay_minutes: Number(filled.maxAttempts) > 1 ? Number(filled.retryDelayMinutes) : (Number(filled.retryDelayMinutes) || 60)
  };
  if (audience.type === 'list') body.list_name = audience.list_name;
  else if (mode === 'edit') body.list_name = null;
  if (audience.type === 'filter') body.filters = filtersPayload(audience.filters);
  else if (mode === 'edit') body.filters = {};
  if (audience.type === 'manual' && mode === 'create') body.lead_ids = uniqueIds(audience.lead_ids);
  if (allowStart && filled.startMode === 'now') {
    if (mode === 'create') body.start_now = true;
    else body.start_at = null;
  } else if (allowStart && filled.startMode === 'later') {
    const start = resolveZonedTime(filled.startDate, filled.startTime, timeZone);
    if (start.ok) body.start_at = start.date.toISOString();
    if (mode === 'create') body.start_now = false;
  }
  if (filled.endDate && filled.endTime) {
    const end = resolveZonedTime(filled.endDate, filled.endTime, timeZone);
    if (end.ok) body.end_at = end.date.toISOString();
  } else if (mode === 'edit') body.end_at = null;
  return body;
}

async function loadMemberIds(api, campaignId) {
  const ids = [];
  let offset = 0;
  let total = null;
  let sawShort = false;
  const limit = 100;
  for (let page = 0; page < 40; page++) {
    const data = await api('GET', '/api/campaigns/' + encodeURIComponent(campaignId) + '/leads?limit=' + limit + '&offset=' + offset);
    const items = (data && data.items) || [];
    if (data && data.total != null) total = num(data.total);
    items.forEach((row) => { if (row && row.lead_id != null) ids.push(row.lead_id); });
    offset += items.length;
    if (items.length < limit) { sawShort = true; break; }
    if (total != null && offset >= total) break;
  }
  const unique = uniqueIds(ids);
  const complete = sawShort || (total != null && unique.length >= total);
  return { ids: unique, total: total == null ? unique.length : total, complete: complete };
}

async function syncManualMembers(api, campaignId, beforeIds, wantIds, complete) {
  const before = new Set(beforeIds.map(String));
  const want = new Set(wantIds.map(String));
  const add = wantIds.filter((id) => !before.has(String(id)));
  const remove = complete ? beforeIds.filter((id) => !want.has(String(id))) : [];
  if (add.length) await api('POST', '/api/campaigns/' + encodeURIComponent(campaignId) + '/leads', { lead_ids: add });
  for (let i = 0; i < remove.length; i++) {
    await api('DELETE', '/api/campaigns/' + encodeURIComponent(campaignId) + '/leads/' + encodeURIComponent(remove[i]));
  }
  return { skippedRemovals: !complete && beforeIds.some((id) => !want.has(String(id))) };
}

export function CampaignWizard({ api, toast, timezone, mode, campaignId, initialLeadIds, onClose, onSaved }) {
  const zone = safeZone(timezone);
  const lookups = useLookups(api);
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(mode !== 'edit');
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [audience, setAudience] = useState(() => emptyAudience(initialLeadIds));
  const [schedule, setSchedule] = useState(() => defaultSchedule());
  const [status, setStatus] = useState('draft');
  const [preview, setPreview] = useState({ key: '', state: 'idle', data: null, error: '' });
  const [members, setMembers] = useState({ ids: [], complete: true, loaded: mode !== 'edit' });
  const [memberNote, setMemberNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const stepRef = useRef(null);
  const titleId = useId();
  const ref = useDialog(saving ? function () {} : onClose);
  const allowStart = mode === 'create' || status === 'draft' || status === 'scheduled';

  useEffect(() => {
    if (mode !== 'edit' || !campaignId) return undefined;
    let cancelled = false;
    api('GET', '/api/campaigns/' + encodeURIComponent(campaignId))
      .then(async (data) => {
        const unwrapped = unwrapCampaign(data);
        const campaign = unwrapped.campaign || {};
        const nextAudience = audienceFromCampaign(campaign);
        let note = '';
        let memberState = { ids: nextAudience.lead_ids, complete: true, loaded: true };
        if (nextAudience.type === 'manual') {
          try {
            const loaded = await loadMemberIds(api, campaignId);
            memberState = { ids: loaded.ids, complete: loaded.complete, loaded: true };
            nextAudience.lead_ids = loaded.ids;
            if (!loaded.complete) note = 'This campaign has more people than we could load. Removing people while editing may be incomplete.';
          } catch (err) {
            memberState = { ids: nextAudience.lead_ids, complete: false, loaded: false };
            note = 'The people already on this campaign could not be loaded. You can change the name and schedule. The people list will not be changed.';
          }
        }
        if (cancelled) return;
        setName(campaign.name || '');
        setAudience(nextAudience);
        setSchedule(scheduleFromCampaign(campaign, zone));
        setStatus(campaign.status || 'draft');
        setMembers(memberState);
        setMemberNote(note);
        setReady(true);
      })
      .catch((err) => { if (!cancelled) setLoadError(errorMessage(err, 'Could not load this campaign.')); });
    return () => { cancelled = true; };
  }, [campaignId, mode]);

  useEffect(() => {
    if (step === 0 || !stepRef.current) return;
    stepRef.current.focus();
  }, [step]);

  useEffect(() => {
    if (!ready || step !== 0 || !ref.current) return;
    const field = ref.current.querySelector('[data-autofocus]');
    if (field && typeof field.focus === 'function') field.focus();
  }, [ready]);

  function audienceBlock() {
    if (audience.type === 'list' && !audience.list_name) return 'Choose an imported list.';
    if (audience.type === 'manual' && !audience.lead_ids.length) return 'Choose at least one person.';
    if (audience.type === 'filter') {
      const problem = filterProblems(audience.filters);
      if (problem) return problem;
      if (!Object.keys(filtersPayload(audience.filters)).length) return 'Choose at least one way to pick leads.';
    }
    const key = previewKey(audience);
    if (!key) return 'Choose who should be called.';
    if (preview.key !== key || preview.state === 'loading' || preview.state === 'idle') return 'Checking who can be called…';
    if (preview.state === 'error') return preview.error || 'Could not check who would be called.';
    if (num(preview.data && preview.data.eligible) < 1) return 'No one can be called with these choices.';
    return '';
  }

  function nameBlock() {
    if (!name.trim()) return 'Give the campaign a name.';
    if (name.trim().length > 120) return 'Use 120 characters or fewer.';
    return '';
  }

  function blockFor(index) {
    if (index === 0) return nameBlock();
    if (index === 1) return audienceBlock();
    if (index === 2) return scheduleProblem(schedule, zone, allowStart);
    return nameBlock() || audienceBlock() || scheduleProblem(schedule, zone, allowStart);
  }

  const block = blockFor(step);
  const primary = step < 3
    ? 'Next'
    : mode === 'edit'
      ? (allowStart && schedule.startMode === 'now' ? 'Save and start' : 'Save changes')
      : (schedule.startMode === 'now' ? 'Create and start' : 'Schedule campaign');

  async function save() {
    if (blockFor(3) || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const body = buildWriteBody({ name: name, audience: audience, schedule: schedule, timeZone: zone, mode: mode, allowStart: allowStart });
      let saved = null;
      if (mode === 'create') {
        const data = await api('POST', '/api/campaigns', body);
        saved = unwrapCampaign(data).campaign || data;
        notify(toast, schedule.startMode === 'now' ? 'Campaign created. Calling will start now.' : 'Campaign scheduled.');
      } else {
        const data = await api('PATCH', '/api/campaigns/' + encodeURIComponent(campaignId), body);
        saved = unwrapCampaign(data).campaign || { id: campaignId };
        if (audience.type === 'manual') {
          if (!members.loaded) throw new Error('The people on this campaign could not be loaded, so that list was not changed.');
          const sync = await syncManualMembers(api, campaignId, members.ids, uniqueIds(audience.lead_ids), members.complete);
          if (sync.skippedRemovals) notify(toast, 'Saved. Some people could not be removed because the full list did not load.', 'error');
        }
        if (allowStart && schedule.startMode === 'now') {
          const started = await api('POST', '/api/campaigns/' + encodeURIComponent(campaignId) + '/start', {});
          saved = unwrapCampaign(started).campaign || saved;
          notify(toast, 'Campaign saved and started.');
        } else notify(toast, 'Campaign saved.');
      }
      if (onSaved) onSaved(saved);
    } catch (err) {
      setSaveError(errorMessage(err, 'Could not save this campaign.'));
      setSaving(false);
    }
  }

  function onSubmit(event) {
    event.preventDefault();
    if (block || saving) return;
    if (step < 3) setStep(step + 1);
    else save();
  }

  return (
    <div className="cmp-overlay" onMouseDown={(event) => { if (!saving && event.target === event.currentTarget) onClose(); }}>
      <div className="cmp-dialog cmp-dialog--wide" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <div className="cmp-dialog-head">
          <h2 id={titleId} className="cmp-dialog-title">{mode === 'edit' ? 'Edit campaign' : 'New campaign'}</h2>
          <button type="button" className="cmp-icon" onClick={onClose} aria-label="Close" disabled={saving}>×</button>
        </div>
        {!ready ? (
          <div className="cmp-dialog-body">{loadError ? <p className="cmp-warn">{loadError}</p> : <p>Loading campaign…</p>}</div>
        ) : (
          <form className="cmp-dialog-form" onSubmit={onSubmit} noValidate>
            <div className="cmp-dialog-body">
              <ol className="cmp-steps">
                {STEPS.map((label, index) => (
                  <li key={label}>
                    <button type="button" className={cx('cmp-step', index === step && 'cmp-step--current', index < step && 'cmp-step--done')} aria-current={index === step ? 'step' : undefined} disabled={index > step} onClick={() => { if (index < step) setStep(index); }}>
                      <span className="cmp-step-num">{index + 1}</span>
                      <span className="cmp-step-label">{label}</span>
                    </button>
                  </li>
                ))}
              </ol>
              <h3 className="cmp-kicker" tabIndex={-1} ref={stepRef}>{STEPS[step]}</h3>
              {step === 0 ? (
                <div className="cmp-field">
                  <label className="cmp-label" htmlFor={titleId + '-name'}>Campaign name</label>
                  <input id={titleId + '-name'} className="cmp-input" data-autofocus maxLength={120} value={name} onChange={(event) => setName(event.target.value)} placeholder="October realtor follow-up" autoComplete="off" />
                  <p className="cmp-hint">A name your team will recognize.</p>
                </div>
              ) : null}
              {step === 1 ? (
                <React.Fragment>
                  {memberNote ? <p className="cmp-warn">{memberNote}</p> : null}
                  <AudiencePicker api={api} value={audience} onChange={setAudience} onPreview={setPreview} users={lookups.users} usersHidden={lookups.usersHidden} lists={lookups.lists} listsState={lookups.listsState} listsError={lookups.listsError} />
                  <PreviewPanel audience={audience} preview={preview} />
                </React.Fragment>
              ) : null}
              {step === 2 ? <ScheduleFields schedule={schedule} onChange={setSchedule} timeZone={zone} allowStart={allowStart} /> : null}
              {step === 3 ? (
                <ReviewStep name={name} audience={audience} schedule={schedule} preview={preview} users={lookups.users} timeZone={zone} allowStart={allowStart} onJump={setStep} />
              ) : null}
              {saveError ? <p className="cmp-warn" role="alert">{saveError}</p> : null}
            </div>
            <div className="cmp-dialog-foot">
              {block ? <p className="cmp-footer-note" role="status">{block}</p> : null}
              {step > 0 ? (
                <button type="button" className="cmp-btn" onClick={() => setStep(step - 1)} disabled={saving}>Back</button>
              ) : (
                <button type="button" className="cmp-btn" onClick={onClose} disabled={saving}>Cancel</button>
              )}
              <button type="submit" className="cmp-btn cmp-btn--primary" disabled={!!block || saving}>{saving ? 'Saving…' : primary}</button>
            </div>
          </form>
        )}
        {!ready ? (
          <div className="cmp-dialog-foot">
            <button type="button" className="cmp-btn" data-autofocus onClick={onClose}>Close</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function idsForAdd(audience, preview) {
  if (audience.type === 'manual') return { ids: uniqueIds(audience.lead_ids), limited: false, eligible: null };
  const data = preview && preview.data;
  return { ids: [], limited: false, eligible: num(data && data.eligible) };
}

function AddLeadsDialog({ api, toast, campaignId, onClose, onAdded }) {
  const lookups = useLookups(api);
  const [audience, setAudience] = useState(() => emptyAudience());
  const [preview, setPreview] = useState({ key: '', state: 'idle', data: null, error: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = previewKey(audience);
  const fresh = preview.key === key && preview.state === 'ready';
  const plan = fresh ? idsForAdd(audience, preview) : { ids: [], limited: false, eligible: 0 };
  let block = '';
  if (audience.type === 'filter') block = filterProblems(audience.filters) || (!Object.keys(filtersPayload(audience.filters)).length ? 'Choose at least one way to pick leads.' : '');
  if (!block && audience.type === 'list' && !audience.list_name) block = 'Choose an imported list.';
  if (!block && audience.type === 'manual' && !audience.lead_ids.length) block = 'Choose at least one person.';
  if (!block && !key) block = 'Choose who to add.';
  if (!block && (!preview || preview.key !== key || preview.state === 'loading' || preview.state === 'idle')) block = 'Checking who can be called…';
  if (!block && preview.state === 'error') block = preview.error;
  if (!block && num(preview.data && preview.data.eligible) < 1) block = 'No one can be added with these choices.';
  if (!block && audience.type === 'manual' && !plan.ids.length) block = 'Those matches are not listed here. Choose people by hand.';
  const addLabel = plan.limited ? 'Add the ' + plan.ids.length + ' shown' : 'Add leads';

  async function submit(event) {
    event.preventDefault();
    if (block || busy) return;
    setBusy(true);
    setError('');
    try {
      const body = audience.type === 'manual' ? { lead_ids: plan.ids } : previewBody(audience);
      const result = await api('POST', '/api/campaigns/' + encodeURIComponent(campaignId) + '/leads', body);
      notify(toast, 'Added ' + people(result && result.added));
      if (onAdded) onAdded();
    } catch (err) {
      setError(errorMessage(err, 'Could not add those leads.'));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add leads"
      wide
      onClose={busy ? function () {} : onClose}
      footer={(
        <React.Fragment>
          {block ? <p className="cmp-footer-note" role="status">{block}</p> : null}
          {plan.limited && fresh ? <p className="cmp-footer-note">This matches {people(plan.eligible)}. We can add the {plan.ids.length} listed here. Narrow the filter, or choose people by hand, to add the rest.</p> : null}
          <button type="button" className="cmp-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form="cmp-add-leads" className="cmp-btn cmp-btn--primary" disabled={!!block || busy}>{busy ? 'Adding…' : addLabel}</button>
        </React.Fragment>
      )}
    >
      <form id="cmp-add-leads" onSubmit={submit} noValidate>
        <AudiencePicker api={api} value={audience} onChange={setAudience} onPreview={setPreview} users={lookups.users} usersHidden={lookups.usersHidden} lists={lookups.lists} listsState={lookups.listsState} listsError={lookups.listsError} />
        <PreviewPanel audience={audience} preview={preview} />
        {error ? <p className="cmp-warn" role="alert">{error}</p> : null}
      </form>
    </Modal>
  );
}

export function CampaignDetail({ api, toast, user, timezone, campaignId, onOpenLead, onClose, onChanged }) {
  const zone = safeZone(timezone);
  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(emptyStats());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [leadStatus, setLeadStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [leads, setLeads] = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsError, setLeadsError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removeRow, setRemoveRow] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const statusRef = useRef('');
  const mutable = canMutate(user);
  const open = campaign && OPEN_STATUSES[String(campaign.status || '').toLowerCase()];

  useEffect(() => {
    let cancelled = false;
    function load(silent) {
      if (!silent) setLoading(true);
      api('GET', '/api/campaigns/' + encodeURIComponent(campaignId))
        .then((data) => {
          if (cancelled) return;
          const unwrapped = unwrapCampaign(data);
          setCampaign(unwrapped.campaign);
          setStats(statsOf(unwrapped.campaign, unwrapped.stats));
          statusRef.current = unwrapped.campaign ? unwrapped.campaign.status : '';
          setError('');
        })
        .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not load this campaign.')); })
        .finally(() => { if (!cancelled && !silent) setLoading(false); });
    }
    load(false);
    const timer = setInterval(() => {
      if (!document.hidden && statusRef.current === 'running') load(true);
    }, 10000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [campaignId, refresh]);

  useEffect(() => {
    let cancelled = false;
    setLeads([]);
    setLeadsLoading(true);
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    if (leadStatus) params.set('status', leadStatus);
    api('GET', '/api/campaigns/' + encodeURIComponent(campaignId) + '/leads?' + params.toString())
      .then((data) => {
        if (cancelled) return;
        setLeads((data && data.items) || []);
        setLeadTotal(num(data && data.total));
        setLeadsError('');
      })
      .catch((err) => { if (!cancelled) setLeadsError(errorMessage(err, 'Could not load the people on this campaign.')); })
      .finally(() => { if (!cancelled) setLeadsLoading(false); });
    const timer = setInterval(() => {
      if (document.hidden || statusRef.current !== 'running') return;
      api('GET', '/api/campaigns/' + encodeURIComponent(campaignId) + '/leads?' + params.toString())
        .then((data) => {
          if (cancelled) return;
          setLeads((data && data.items) || []);
          setLeadTotal(num(data && data.total));
        })
        .catch(() => {});
    }, 10000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [campaignId, leadStatus, offset, refresh]);

  function changed() { if (onChanged) onChanged(); }

  async function run(action) {
    setBusy(true);
    try {
      if (action === 'delete') {
        await api('DELETE', '/api/campaigns/' + encodeURIComponent(campaignId));
        notify(toast, 'Campaign deleted.');
        changed();
        onClose();
        return;
      }
      await api('POST', '/api/campaigns/' + encodeURIComponent(campaignId) + '/' + action, {});
      notify(toast, actionToast(action));
      setConfirm(null);
      setRefresh((n) => n + 1);
      changed();
    } catch (err) {
      notify(toast, errorMessage(err, 'That did not work.'), 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  function onAction(row, action) {
    if (action === 'edit') { setEditing(true); return; }
    if (action === 'stop' || action === 'delete') { setConfirm({ action: action, name: row.name }); return; }
    run(action);
  }

  async function removeLead() {
    if (!removeRow) return;
    setBusy(true);
    try {
      await api('DELETE', '/api/campaigns/' + encodeURIComponent(campaignId) + '/leads/' + encodeURIComponent(removeRow.lead_id));
      notify(toast, 'Removed from the campaign.');
      setRemoveRow(null);
      setRefresh((n) => n + 1);
      if (leads.length === 1 && offset > 0) setOffset(Math.max(0, offset - PAGE_SIZE));
      changed();
    } catch (err) {
      notify(toast, errorMessage(err, 'Could not remove that person.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const tiles = [
    ['Total', formatCount(stats.total)],
    ['Called', formatCount(stats.dialed)],
    ['Connected', formatCount(stats.connected)],
    ['Interested', formatCount(stats.interested)],
    ['Still to call', formatCount(stats.remaining)],
    ['Minutes', formatCount(stats.minutes)],
    ['Conversion', formatPct(stats.conversion_pct)]
  ];
  const pageFrom = leadTotal ? offset + 1 : 0;
  const pageTo = Math.min(offset + leads.length, leadTotal);

  return (
    <div className="cmp-detail">
      <button type="button" className="cmp-back" onClick={onClose}>All campaigns</button>
      {loading && !campaign ? <p>Loading campaign…</p> : null}
      {error && !campaign ? (
        <div className="cmp-error" role="alert">
          <p>{error}</p>
          <button type="button" className="cmp-btn" onClick={onClose}>Back</button>
        </div>
      ) : null}
      {campaign ? (
        <React.Fragment>
          <header className="cmp-header">
            <div>
              <div className="cmp-title-row">
                <h1 className="cmp-title">{campaign.name}</h1>
                <StatusPill status={campaign.status} />
              </div>
              <p className="cmp-sub">{audienceBlurb(campaign)} · {timingBlurb(campaign, zone)}</p>
              {campaign.end_at ? <p className="cmp-hint">Stops {showWhen(campaign.end_at, zone)}</p> : null}
            </div>
            <CampaignActions campaign={campaign} user={user} busy={busy} onAction={onAction} />
          </header>
          <div className="cmp-tiles">
            {tiles.map(([label, value]) => (
              <div key={label} className="cmp-tile">
                <div className="cmp-tile-value">{value}</div>
                <div className="cmp-tile-label">{label}</div>
              </div>
            ))}
          </div>
          <ProgressBar stats={stats} />
          {campaign.status === 'running' ? <p className="cmp-hint">These numbers refresh about every 10 seconds while the campaign is running.</p> : null}
          <div className="cmp-toolbar">
            <div className="cmp-tabs" role="tablist" aria-label="Lead status">
              {LEAD_TABS.map(([id, label]) => (
                <button key={label} type="button" role="tab" aria-selected={leadStatus === id} className={cx('cmp-tab', leadStatus === id && 'cmp-tab--on')} onClick={() => { setLeadStatus(id); setOffset(0); }}>{label}</button>
              ))}
            </div>
            {mutable && open ? <button type="button" className="cmp-btn cmp-btn--primary" onClick={() => setAdding(true)}>Add leads</button> : null}
          </div>
          {leadsError ? <p className="cmp-warn" role="alert">{leadsError}</p> : null}
          {leadsLoading && !leads.length ? <p>Loading people…</p> : null}
          {!leadsLoading && !leads.length && !leadsError ? <p className="cmp-empty-inline">No one in this campaign matches this filter.</p> : null}
          {leads.length ? (
            <div className="cmp-scroll">
              <table className="cmp-table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Company</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Status</th>
                    <th scope="col">Tries</th>
                    <th scope="col">Last result</th>
                    <th scope="col">Last try</th>
                    <th scope="col">Next try</th>
                    {mutable && open ? <th scope="col"><span className="cmp-sr">Actions</span></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((row) => (
                    <tr key={row.lead_id}>
                      <td>
                        {onOpenLead ? (
                          <button type="button" className="cmp-link" onClick={() => onOpenLead(row.lead_id)}>{row.name || 'Unnamed lead'}</button>
                        ) : (row.name || 'Unnamed lead')}
                      </td>
                      <td>{row.company || '—'}</td>
                      <td>{row.phone ? <a className="cmp-link" href={'tel:' + row.phone}>{formatPhone(row.phone)}</a> : '—'}</td>
                      <td>{labelOf(row.status)}</td>
                      <td>{row.attempts != null ? row.attempts : '—'}</td>
                      <td>{row.last_outcome ? labelOf(row.last_outcome) : '—'}</td>
                      <td>{showWhen(row.last_attempt_at, zone)}</td>
                      <td>{showWhen(row.next_attempt_at, zone)}</td>
                      {mutable && open ? (
                        <td><button type="button" className="cmp-btn cmp-btn--danger" onClick={() => setRemoveRow(row)}>Remove</button></td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {leadTotal > PAGE_SIZE ? (
            <div className="cmp-pager">
              <button type="button" className="cmp-btn" disabled={offset <= 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
              <span>{pageFrom}–{pageTo} of {leadTotal}</span>
              <button type="button" className="cmp-btn" disabled={offset + PAGE_SIZE >= leadTotal} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
            </div>
          ) : null}
        </React.Fragment>
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={confirm.action === 'delete' ? 'Delete this campaign?' : 'Stop this campaign?'}
          body={confirm.action === 'delete' ? 'Delete “' + (confirm.name || 'this campaign') + '”? This cannot be undone.' : 'Anyone who has not been called yet will be left alone.'}
          confirmLabel={confirm.action === 'delete' ? 'Delete campaign' : 'Stop campaign'}
          busy={busy}
          onConfirm={() => run(confirm.action)}
          onClose={() => { if (!busy) setConfirm(null); }}
        />
      ) : null}
      {removeRow ? (
        <ConfirmDialog
          title="Remove this person?"
          body={'Remove ' + (removeRow.name || 'this person') + ' from the campaign? They will not be called as part of it.'}
          confirmLabel="Remove"
          busy={busy}
          onConfirm={removeLead}
          onClose={() => { if (!busy) setRemoveRow(null); }}
        />
      ) : null}
      {adding ? <AddLeadsDialog api={api} toast={toast} campaignId={campaignId} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); setRefresh((n) => n + 1); changed(); }} /> : null}
      {editing ? (
        <CampaignWizard
          api={api}
          toast={toast}
          timezone={zone}
          mode="edit"
          campaignId={campaignId}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); setRefresh((n) => n + 1); changed(); }}
        />
      ) : null}
    </div>
  );
}

export function CampaignsView({ api, toast, user, timezone, onOpenLead }) {
  const zone = safeZone(timezone);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stale, setStale] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const itemsRef = useRef([]);
  itemsRef.current = items;

  useEffect(() => {
    let cancelled = false;
    const silent = reloadKey > 0 && itemsRef.current.length > 0;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    api('GET', '/api/campaigns')
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data && data.items) ? data.items : []);
        setError('');
        setStale('');
      })
      .catch((err) => {
        if (cancelled) return;
        if (silent && itemsRef.current.length) setStale('Couldn’t refresh. Showing the last update.');
        else setError(errorMessage(err, 'Could not load campaigns.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.hidden) return;
      if (itemsRef.current.some((item) => item.status === 'running')) setReloadKey((n) => n + 1);
    }, 10000);
    function onVisible() {
      if (!document.hidden && itemsRef.current.some((item) => item.status === 'running')) setReloadKey((n) => n + 1);
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  async function run(campaign, action) {
    setBusyId(campaign.id);
    try {
      if (action === 'delete') await api('DELETE', '/api/campaigns/' + encodeURIComponent(campaign.id));
      else await api('POST', '/api/campaigns/' + encodeURIComponent(campaign.id) + '/' + action, {});
      notify(toast, actionToast(action));
      setConfirm(null);
      if (detailId && sameId(detailId, campaign.id) && action === 'delete') setDetailId(null);
      setReloadKey((n) => n + 1);
    } catch (err) {
      notify(toast, errorMessage(err, 'That did not work.'), 'error');
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  }

  function onAction(campaign, action) {
    if (action === 'edit') { setWizard({ mode: 'edit', id: campaign.id }); return; }
    if (action === 'stop' || action === 'delete') { setConfirm({ campaign: campaign, action: action }); return; }
    run(campaign, action);
  }

  const mutable = canMutate(user);
  const blocked = needsTenant(user);

  return (
    <div className="cmp">
      {detailId ? (
        <CampaignDetail
          api={api}
          toast={toast}
          user={user}
          timezone={zone}
          campaignId={detailId}
          onOpenLead={onOpenLead}
          onClose={() => setDetailId(null)}
          onChanged={() => setReloadKey((n) => n + 1)}
        />
      ) : (
        <React.Fragment>
          <header className="cmp-header">
            <div>
              <h1 className="cmp-title">Campaigns</h1>
              <p className="cmp-sub">A campaign calls a group of your leads on the days and hours you pick.</p>
            </div>
            <div className="cmp-header-actions">
              <button type="button" className="cmp-btn" onClick={() => setReloadKey((n) => n + 1)}>Refresh</button>
              {mutable ? <button type="button" className="cmp-btn cmp-btn--primary" disabled={blocked} onClick={() => setWizard({ mode: 'create' })}>New campaign</button> : null}
            </div>
          </header>
          {blocked ? <p className="cmp-banner">Switch to a workspace before you build a campaign. Use View as on a tenant, then come back here.</p> : null}
          {!mutable ? <p className="cmp-note">You can look at campaigns. An admin can create or change them.</p> : null}
          {stale ? <p className="cmp-warn" role="status">{stale}</p> : null}
          {loading && !items.length ? <p>Loading campaigns…</p> : null}
          {error && !items.length ? (
            <div className="cmp-error" role="alert">
              <p>{error}</p>
              <button type="button" className="cmp-btn" onClick={() => setReloadKey((n) => n + 1)}>Try again</button>
            </div>
          ) : null}
          {!loading && !error && !items.length ? (
            <div className="cmp-empty">
              <h2>No campaigns yet</h2>
              <p>A campaign calls a group of leads for you. You choose who to call, which days, and what hours. We place the calls and show you who answered and who was interested.</p>
              {mutable ? <button type="button" className="cmp-btn cmp-btn--primary" disabled={blocked} onClick={() => setWizard({ mode: 'create' })}>New campaign</button> : null}
            </div>
          ) : null}
          {items.length ? (
            <div className="cmp-list">
              {items.map((campaign) => {
                const stats = statsOf(campaign);
                return (
                  <article key={campaign.id} className="cmp-card">
                    <div className="cmp-card-top">
                      <button type="button" className="cmp-card-name" onClick={() => setDetailId(campaign.id)}>{campaign.name || 'Untitled campaign'}</button>
                      <StatusPill status={campaign.status} />
                    </div>
                    <p className="cmp-audience">{audienceBlurb(campaign)}</p>
                    <ProgressBar stats={stats} />
                    <dl className="cmp-metrics">
                      <div><dt>Connected</dt><dd>{formatCount(stats.connected)}</dd></div>
                      <div><dt>Interested</dt><dd>{formatCount(stats.interested)}</dd></div>
                      <div><dt>Conversion</dt><dd>{formatPct(stats.conversion_pct)}</dd></div>
                    </dl>
                    <p className="cmp-when">{timingBlurb(campaign, zone)}</p>
                    <div className="cmp-card-actions">
                      <button type="button" className="cmp-btn" onClick={() => setDetailId(campaign.id)}>View</button>
                      <CampaignActions campaign={campaign} user={user} busy={busyId != null} onAction={onAction} />
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </React.Fragment>
      )}
      {wizard ? (
        <CampaignWizard
          api={api}
          toast={toast}
          timezone={zone}
          mode={wizard.mode}
          campaignId={wizard.id}
          onClose={() => setWizard(null)}
          onSaved={(saved) => {
            setWizard(null);
            setReloadKey((n) => n + 1);
            if (saved && saved.id) setDetailId(saved.id);
          }}
        />
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={confirm.action === 'delete' ? 'Delete this campaign?' : 'Stop this campaign?'}
          body={confirm.action === 'delete' ? 'Delete “' + (confirm.campaign.name || 'this campaign') + '”? This cannot be undone.' : 'Anyone who has not been called yet will be left alone.'}
          confirmLabel={confirm.action === 'delete' ? 'Delete campaign' : 'Stop campaign'}
          busy={busyId != null}
          onConfirm={() => run(confirm.campaign, confirm.action)}
          onClose={() => { if (busyId == null) setConfirm(null); }}
        />
      ) : null}
    </div>
  );
}

export function AddToCampaignModal({ api, toast, leadIds, onClose, onDone, timezone }) {
  const ids = uniqueIds(Array.isArray(leadIds) ? leadIds : (leadIds ? Array.from(leadIds) : []));
  const zone = safeZone(timezone);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const uid = useId();

  useEffect(() => {
    let cancelled = false;
    api('GET', '/api/campaigns')
      .then((data) => {
        if (cancelled) return;
        const rows = ((data && data.items) || []).filter((item) => OPEN_STATUSES[String(item.status || '').toLowerCase()]);
        setItems(rows);
        if (rows[0]) setPicked(String(rows[0].id));
      })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not load campaigns.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function addExisting(event) {
    event.preventDefault();
    if (!picked || busy) return;
    setBusy(true);
    setError('');
    try {
      await api('POST', '/api/campaigns/' + encodeURIComponent(picked) + '/leads', { lead_ids: ids });
      notify(toast, 'Added to the campaign.');
      if (onDone) onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not add those leads.'));
      setBusy(false);
    }
  }

  if (creating) {
    return (
      <CampaignWizard
        api={api}
        toast={toast}
        timezone={zone}
        mode="create"
        initialLeadIds={ids}
        onClose={() => setCreating(false)}
        onSaved={() => { if (onDone) onDone(); }}
      />
    );
  }

  return (
    <Modal
      title="Add to a campaign"
      onClose={busy ? function () {} : onClose}
      footer={!ids.length ? (
        <button type="button" className="cmp-btn" data-autofocus onClick={onClose}>Close</button>
      ) : (
        <React.Fragment>
          <button type="button" className="cmp-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="cmp-btn" onClick={() => setCreating(true)} disabled={busy}>New campaign</button>
          <button type="submit" form={uid + '-add'} className="cmp-btn cmp-btn--primary" disabled={busy || !picked || !items.length}>{busy ? 'Adding…' : 'Add to campaign'}</button>
        </React.Fragment>
      )}
    >
      {!ids.length ? <p>Select at least one lead on the Leads page first.</p> : (
        <form id={uid + '-add'} onSubmit={addExisting}>
          <p className="cmp-sub">Add {people(ids.length)} to a campaign that is still open, or start a new one with them already included.</p>
          {loading ? <p>Loading campaigns…</p> : null}
          {error ? <p className="cmp-warn" role="alert">{error}</p> : null}
          {!loading && !items.length && !error ? <p>No campaign is ready for more people. Create a new one.</p> : null}
          {items.length ? (
            <div className="cmp-choices" role="radiogroup" aria-label="Choose a campaign">
              {items.map((item) => (
                <label key={item.id} className={cx('cmp-choice', String(picked) === String(item.id) && 'cmp-choice--on')}>
                  <input type="radio" name={uid + '-camp'} value={item.id} checked={String(picked) === String(item.id)} onChange={() => setPicked(String(item.id))} />
                  <span>
                    <span className="cmp-choice-title">{item.name || 'Untitled campaign'}</span>
                    <span className="cmp-choice-copy">{labelOf(item.status)} · {audienceBlurb(item)}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : null}
        </form>
      )}
    </Modal>
  );
}
